import debug from "debug";
import Experience from "../Experience";
import MakePart from "./MakePart";
import MakePartsPreprocessor from "./MakePartsPreprocessor";

const log = debug("app:assets");

/**
 * The `MakeParts` class handles the creation and processing of parts using event-driven architecture.
 * It listens to various events related to part creation, processes the data, and initializes parts accordingly.
 * @category Experience
 * @subcategory World
 */
export default class MakeParts {
  /**
   * Constructs an instance of `MakeParts` and initializes event listeners.
   */
  constructor() {
    log("initiated");
    this.experience = new Experience();
    this.eventHub = this.experience.eventHub;
    this.viewMode = { viewMode: "hybrid", "3d": false };
    this.init();
  }

  cleanup() {
    try {
      log("Cleaning up MakeParts...");
  
      // Remove event listeners
      this.eventHub.off("makeParts", this.processXmdData);
      this.eventHub.off("makePartsLoader", this.processXmdData);
      this.eventHub.off("mode-change", this.viewMode);
      this.eventHub.off("3d-enabled", this.viewMode);
  
      // Traverse and dispose of Three.js objects
      this.experience.scene.traverse((object) => {
        if (object.isMesh) {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((mat) => mat.dispose());
            } else {
              object.material.dispose();
            }
          }
        }
      });
  
      // Clear references
      this.viewMode = null;
      this.experience = null;
      this.eventHub = null;
  
      log("MakeParts cleanup completed.");
    } catch (error) {
      console.error("Error during MakeParts cleanup:", error);
    }
  }

  /**
   * Initializes event listeners for processing part creation requests and mode changes.
   */
  init() {
    log("MakeParts is listening for makeParts event");
    this.eventHub.on("makeParts", (xmdData) =>
      this.processXmdData(xmdData, "makeParts"),
    );
    this.eventHub.on("makePartsLoader", (xmdData) =>
      this.processXmdData(xmdData, "makePartsLoader"),
    );
    this.eventHub.on("mode-change", (event) => {
      this.viewMode.viewMode = event;
      log("viewMode = ", this.viewMode);
    });
    this.eventHub.on("3d-enabled", (event) => {
      this.viewMode["3d"] = event.enabled;
      log("viewMode = ", this.viewMode);
    });
  }

  /**
   * Processes incoming XMD data from events and initializes the required parts.
   * @param {object} xmdData - The data received from an XMD-related event.
   * @param {string} eventName - The name of the event triggering this function.
   * @returns {Promise<void>} - A promise that resolves when the processing is complete.
   */
  async processXmdData(xmdData, eventName) {
    log("xmdData", xmdData);
    let xmd;

    xmd = {
      figures: xmdData.figures,
      others: xmdData.others,
      version: xmdData.version || "2.0",
    };
    
    // Show loading spinner when starting to process parts (not for loading existing projects)
    if (eventName === "makeParts" && xmd.figures && xmd.figures.length > 0) {
      const partNumber = xmd.figures[0].partData?.fullpartnumber || 
                        xmd.figures[0].partData?.partNumber || 
                        "part";
      log('Starting part loading for:', partNumber);
      this.eventHub.emit("part-loading-start", {
        id: 'current-part-loading', // Use consistent ID
        text: `Loading ${partNumber}...`
      });
    }

    let processedXmd;
    if (eventName === "makePartsLoader") {
      processedXmd = await new MakePartsPreprocessor().init({
        method: "process",
        arg: xmd,
      });
    } else {
      processedXmd = xmd;
    }

    if (processedXmd.others) {
      processedXmd.others.forEach((textboxConfig) => {
        log("textbox config", textboxConfig);
        this.eventHub.emit("textBox", textboxConfig);
      });
    }

    log("eventname", eventName, "processedXMD", processedXmd);
    const version = processedXmd.version || "2.0";
    const viewMode = this.viewMode;
    const figures = processedXmd.figures;

    log("version", version);
    log("viewMode", viewMode);
    log("figure", figures);

    const promises = figures.map(async (figure) => {
      try {
        if (!figure.partData.mode) {
          figure.partData.mode = "xm-part";
        }
        return await new MakePart().init(figure, viewMode, version);
      } catch (error) {
        return { error, figure };
      }
    });

    const results = await Promise.allSettled(promises);

    // Emit "xmd" event after all promises have settled and after a small delay
    setTimeout(() => {
      log("xmd", {
        results,
        successes: results.filter((result) => result.status === "fulfilled")
          .length,
        failures: results.filter((result) => result.status === "rejected")
          .length,
      });
      this.eventHub.emit("xmd", {
        results,
        successes: results.filter((result) => result.status === "fulfilled")
          .length,
        failures: results.filter((result) => result.status === "rejected")
          .length,
      });

      this.eventHub.emit("loading-finished");
    }, 200); // 100 milliseconds delay to ensure everything has settledc

    if (eventName === "makePartsLoader") {
      //this.eventHub.emit("zoomAll");
    }
  }
}
